/** biome-ignore-all lint/style/noNestedTernary: channel icons require nested ternary */

import type { Channel, MuteDuration } from "@elaso/shared/types/db";
import { DEFAULT_WORKSPACE_SCHEDULE_SETTINGS } from "@elaso/shared/types/db";
import {
	Bell,
	BellOff,
	CalendarDays,
	CheckCheck,
	ChevronDown,
	MessageSquare,
	MessageSquareText,
	Mic,
	MicOff,
	Presentation,
	Video,
	VideoOff,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSub,
	ContextMenuSubContent,
	ContextMenuSubTrigger,
	ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	useAccessibleChannels,
	useSelfPermissionsOnChannel,
} from "@/hooks/use-accessible-channels";
import {
	useCategoryVideoParticipants,
	useChannelParticipants,
} from "@/hooks/use-presence";
import { useHasSchedule } from "@/hooks/use-user-schedule";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";
import { ws } from "@/lib/websocket-wrapper";
import { ChannelParticipants } from "../channel-participants";
import { PullTogetherDialog } from "../pull-together-dialog";

type ChannelItemProps = {
	channel: Channel;
	hasUnread: boolean;
	mentionCount: number;
	isBroadcasting: boolean;
	onChannelClick: (channel: Channel, e: React.MouseEvent) => void;
	onChannelKeyDown: (channel: Channel, e: React.KeyboardEvent) => void;
	onTextChatClick: (channelId: string, e: React.MouseEvent) => void;
	onCloseThread?: (threadId: string) => void;
	threads?: Channel[];
	maxParticipants?: number | null;
	hasUnreadFn?: (channelId: string) => boolean;
	channelMentionCounts?: Record<string, number>;
	isActive?: boolean;
	isConnectedVoice?: boolean;
	isTextChatActive?: boolean;
	isMuted?: boolean;
	isEffectivelyMuted?: (channelId: string) => boolean;
	onMute?: (
		targetId: string,
		targetType: "channel" | "category",
		duration: MuteDuration
	) => void;
	onUnmute?: (targetId: string, targetType: "channel" | "category") => void;
};

const MAX_MENTION_DISPLAY = 9;
const THREAD_ACTIVITY_THRESHOLD_HOURS = 24;
const THREAD_NAME_MAX_CHARS = 15;

function ChannelItem({
	channel,
	hasUnread,
	mentionCount,
	isBroadcasting,
	onChannelClick,
	onChannelKeyDown,
	onTextChatClick,
	onCloseThread,
	threads = [],
	maxParticipants,
	hasUnreadFn,
	channelMentionCounts = {},
	isActive = false,
	isConnectedVoice = false,
	isTextChatActive = false,
	isMuted = false,
	isEffectivelyMuted,
	onMute,
	onUnmute,
}: ChannelItemProps) {
	const [pullTogetherOpen, setPullTogetherOpen] = useState(false);
	const isVoiceChannel = channel.type === "voice_and_text";
	const isTextChannel = channel.type === "text";
	const isStageChannel = channel.type === "stage";
	const isClickable = isVoiceChannel || isTextChannel || isStageChannel;
	const isDisabled = isBroadcasting && (isVoiceChannel || isStageChannel);
	const showUnread = hasUnread && !isMuted;
	const isVoiceOrStage = isVoiceChannel || isStageChannel;
	const permissionLevel = useSelfPermissionsOnChannel(channel.id);
	// TODO: Re-enable Pull People for stage channels once the feature is ready
	const showPullTogether = isVoiceChannel && permissionLevel === "moderate";

	const participants = useChannelParticipants(
		isVoiceChannel || isStageChannel ? channel.id : null
	);
	const participantCount = participants.length;
	const isFull = maxParticipants != null && participantCount >= maxParticipants;

	// A voice or stage connection shows a green icon rather than highlighting the
	// row, so only an active text channel highlights.
	const shouldHighlightRow =
		isVoiceChannel || isStageChannel ? false : isActive;

	const className = cn(
		"group/item group relative flex flex-1 items-center gap-1 rounded-md px-2 py-1",
		isClickable &&
			!isDisabled &&
			!isFull &&
			"cursor-pointer hover:bg-accent dark:hover:bg-accent/70",
		(isDisabled || isFull) && "cursor-not-allowed opacity-50",
		shouldHighlightRow && "bg-accent/75 dark:bg-accent/50"
	);

	const hasThreads = threads.length > 0;

	const channelRow = (
		<div className="relative flex w-full items-center gap-1">
			{/** biome-ignore lint/a11y/useSemanticElements: using div for custom interactive element */}
			<div
				className={className}
				onClick={(e) => !isFull && onChannelClick(channel, e)}
				onKeyDown={(e) => !isFull && onChannelKeyDown(channel, e)}
				role="button"
				tabIndex={isDisabled || isFull ? -1 : 0}
			>
				{isStageChannel ? (
					<Presentation
						className={cn(
							"mr-1 size-3.5",
							isConnectedVoice && "text-green-600"
						)}
					/>
				) : isVoiceChannel ? (
					<Video
						className={cn(
							"mr-1 size-3.5",
							isConnectedVoice && "text-green-600"
						)}
					/>
				) : showUnread ? (
					<MessageSquareText className="mr-1 size-3.5" />
				) : (
					<MessageSquare className="mr-1 size-3.5" />
				)}
				<span
					className={cn(
						"flex-1 text-muted-foreground text-sm group-hover/item:text-foreground",
						(isActive || showUnread || isConnectedVoice) && "text-foreground"
					)}
				>
					{channel.name}
				</span>
				{isVoiceChannel && maxParticipants != null && (
					<span
						className={cn(
							"text-muted-foreground text-xs",
							isFull && "text-destructive"
						)}
					>
						({participantCount}/{maxParticipants})
					</span>
				)}
			</div>
			{isVoiceOrStage && (
				<div className="relative">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									"size-8 cursor-pointer hover:bg-accent hover:text-accent-foreground",
									isTextChatActive && "bg-accent/75 dark:bg-accent/50"
								)}
								onClick={(e) => onTextChatClick(channel.id, e)}
								size="icon"
								variant="ghost"
							>
								{showUnread ? (
									<MessageSquareText className="size-3" />
								) : (
									<MessageSquare className="size-3" />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent>Open text chat</TooltipContent>
					</Tooltip>
				</div>
			)}
			{showUnread && (
				<div className="-translate-y-1/2 -left-3 absolute top-1/2 size-2 rotate-45 rounded-xs bg-foreground" />
			)}
		</div>
	);

	const muteSubmenu =
		onMute &&
		onUnmute &&
		(isMuted ? (
			<ContextMenuItem onClick={() => onUnmute(channel.id, "channel")}>
				<Bell className="size-4" />
				Unmute Channel
			</ContextMenuItem>
		) : (
			<ContextMenuSub>
				<ContextMenuSubTrigger>
					<BellOff className="size-4" />
					Mute Channel
				</ContextMenuSubTrigger>
				<ContextMenuSubContent>
					<ContextMenuItem onClick={() => onMute(channel.id, "channel", "15m")}>
						For 15 Minutes
					</ContextMenuItem>
					<ContextMenuItem onClick={() => onMute(channel.id, "channel", "1h")}>
						For 1 Hour
					</ContextMenuItem>
					<ContextMenuItem onClick={() => onMute(channel.id, "channel", "8h")}>
						For 8 Hours
					</ContextMenuItem>
					<ContextMenuItem onClick={() => onMute(channel.id, "channel", "24h")}>
						For 24 Hours
					</ContextMenuItem>
					<ContextMenuItem
						onClick={() => onMute(channel.id, "channel", "indefinite")}
					>
						Until I Turn it Back On
					</ContextMenuItem>
				</ContextMenuSubContent>
			</ContextMenuSub>
		));

	const mentionBadge = mentionCount > 0 && (
		<span className="-translate-y-1/2 absolute top-1/2 right-2 z-10 flex size-3.5 items-center justify-center rounded-full bg-red-500/80 text-[0.6rem] text-bold text-white">
			{mentionCount <= MAX_MENTION_DISPLAY ? mentionCount : "9+"}
		</span>
	);

	return (
		<SidebarMenuItem className="relative">
			<div className="flex w-full flex-col gap-0">
				<div
					className={cn(
						"transition-opacity",
						isMuted && !isActive && "opacity-60 hover:opacity-100"
					)}
				>
					<ContextMenu>
						<ContextMenuTrigger asChild>{channelRow}</ContextMenuTrigger>
						<ContextMenuContent>
							{showPullTogether && (
								<ContextMenuItem onClick={() => setPullTogetherOpen(true)}>
									Pull People...
								</ContextMenuItem>
							)}
							{muteSubmenu}
						</ContextMenuContent>
					</ContextMenu>
					{(isVoiceChannel || isStageChannel) && (
						<ChannelParticipants channelId={channel.id} />
					)}
				</div>

				{hasThreads && (
					<div className="relative flex flex-col">
						{threads.map((thread, index) => {
							const isLast = index === threads.length - 1;
							const threadHasUnread = hasUnreadFn?.(thread.id) ?? false;
							const threadMentionCount = channelMentionCounts[thread.id] ?? 0;

							const threadIsMuted = isEffectivelyMuted?.(thread.id) ?? false;
							const threadShowUnread = threadHasUnread && !threadIsMuted;

							return (
								<ContextMenu key={thread.id}>
									<ContextMenuTrigger asChild>
										<div className="group relative flex min-w-0 items-center">
											<div
												className={cn(
													"relative flex min-w-0 flex-1 items-center transition-opacity",
													threadIsMuted && "opacity-60 hover:opacity-100"
												)}
											>
												<div
													className={cn(
														"absolute top-0 bottom-0 w-6",
														isVoiceChannel ? "right-4" : "left-3"
													)}
												>
													<div
														className={cn(
															"absolute top-0 w-[2px] bg-border",
															isLast ? "h-1/4" : "h-full",
															isVoiceChannel ? "right-0" : "left-0"
														)}
													/>
													<div
														className={cn(
															"absolute top-1/4 mt-[0px] h-2 w-4 border-border border-b-[2px]",
															isVoiceChannel
																? "right-0 rounded-br-xl border-r-[2px]"
																: "left-0 rounded-bl-xl border-l-[2px]"
														)}
													/>
												</div>

												{threadShowUnread && (
													<div className="-translate-y-1/2 -left-2.5 absolute top-1/2 size-1.5 rotate-45 rounded-xs bg-foreground" />
												)}

												<button
													className={cn(
														"z-10 flex w-0 flex-1 cursor-pointer items-center gap-1.5 truncate rounded py-0.5 text-left transition-colors hover:bg-accent dark:hover:bg-accent/50",
														isVoiceChannel
															? "mr-8 pr-2 pl-10"
															: "ml-12 pr-2 pl-2"
													)}
													onClick={(e) => onChannelClick(thread, e)}
													onKeyDown={(e) => onChannelKeyDown(thread, e)}
													type="button"
												>
													{threadShowUnread ? (
														<MessageSquareText className="size-3 flex-shrink-0" />
													) : (
														<MessageSquare className="size-3 flex-shrink-0" />
													)}
													<span
														className={cn(
															"shrink grow truncate pl-1 text-muted-foreground text-sm",
															threadShowUnread && "text-foreground"
														)}
													>
														{thread.name.length > THREAD_NAME_MAX_CHARS
															? `${thread.name.slice(0, THREAD_NAME_MAX_CHARS)}...`
															: thread.name}
													</span>
												</button>
											</div>

											{threadMentionCount > 0 && (
												<span
													className={cn(
														"-translate-y-1/2 absolute top-1/2 flex size-3.5 items-center justify-center rounded-full bg-red-500/80 text-[0.6rem] text-bold text-white",
														isVoiceChannel ? "right-9" : "right-2"
													)}
												>
													{threadMentionCount <= MAX_MENTION_DISPLAY
														? threadMentionCount
														: "9+"}
												</span>
											)}
										</div>
									</ContextMenuTrigger>
									<ContextMenuContent>
										<ContextMenuItem onClick={() => onCloseThread?.(thread.id)}>
											Close Thread
										</ContextMenuItem>
										{onMute &&
											onUnmute &&
											(threadIsMuted ? (
												<ContextMenuItem
													onClick={() => onUnmute(thread.id, "channel")}
												>
													<Bell className="size-4" />
													Unmute Thread
												</ContextMenuItem>
											) : (
												<ContextMenuSub>
													<ContextMenuSubTrigger>
														<BellOff className="size-4" />
														Mute Thread
													</ContextMenuSubTrigger>
													<ContextMenuSubContent>
														<ContextMenuItem
															onClick={() =>
																onMute(thread.id, "channel", "15m")
															}
														>
															For 15 Minutes
														</ContextMenuItem>
														<ContextMenuItem
															onClick={() => onMute(thread.id, "channel", "1h")}
														>
															For 1 Hour
														</ContextMenuItem>
														<ContextMenuItem
															onClick={() => onMute(thread.id, "channel", "8h")}
														>
															For 8 Hours
														</ContextMenuItem>
														<ContextMenuItem
															onClick={() =>
																onMute(thread.id, "channel", "24h")
															}
														>
															For 24 Hours
														</ContextMenuItem>
														<ContextMenuItem
															onClick={() =>
																onMute(thread.id, "channel", "indefinite")
															}
														>
															Until I Turn it Back On
														</ContextMenuItem>
													</ContextMenuSubContent>
												</ContextMenuSub>
											))}
									</ContextMenuContent>
								</ContextMenu>
							);
						})}
					</div>
				)}
			</div>
			{isTextChannel && mentionBadge}
			{mentionCount > 0 && isVoiceOrStage && (
				<span className="absolute top-0 right-0.5 flex size-3.5 items-center justify-center rounded-full bg-red-500/80 text-[0.6rem] text-bold text-white">
					{mentionCount <= MAX_MENTION_DISPLAY ? mentionCount : "9+"}
				</span>
			)}
			{showPullTogether && (
				<PullTogetherDialog
					channelId={channel.id}
					onOpenChange={setPullTogetherOpen}
					open={pullTogetherOpen}
				/>
			)}
		</SidebarMenuItem>
	);
}

type CategoryCollapsibleProps = {
	categoryId: string | null;
	categoryName: string;
	categoryHasUnread: boolean;
	channelsWithMentions: Channel[];
	currentChannel: Channel | null;
	textChannelId: string | null;
	connectedVoiceChannelId: string | null;
	children: React.ReactNode;
	hasUnread: (channelId: string) => boolean;
	channelMentionCounts: Record<string, number>;
	isBroadcasting: boolean;
	onChannelClick: (channel: Channel, e: React.MouseEvent) => void;
	onChannelKeyDown: (channel: Channel, e: React.KeyboardEvent) => void;
	onTextChatClick: (channelId: string, e: React.MouseEvent) => void;
	maxParticipants?: number | null;
	videoChannelIds: string[];
	videoChannels: Channel[];
	isCategoryMuted?: boolean;
	onMute?: (
		targetId: string,
		targetType: "channel" | "category",
		duration: MuteDuration
	) => void;
	onUnmute?: (targetId: string, targetType: "channel" | "category") => void;
};

const MAX_COLLAPSED_AVATARS = 10;

function CategoryCollapsible({
	categoryId,
	categoryName,
	categoryHasUnread,
	channelsWithMentions,
	currentChannel,
	textChannelId,
	connectedVoiceChannelId,
	children,
	hasUnread,
	channelMentionCounts,
	isBroadcasting,
	onChannelClick,
	onChannelKeyDown,
	onTextChatClick,
	maxParticipants,
	videoChannelIds,
	videoChannels,
	isCategoryMuted = false,
	onMute,
	onUnmute,
}: CategoryCollapsibleProps) {
	const collapsedCategories = useStore(uiStore, (s) => s.collapsedCategories);
	const setCategoryCollapsed = useStore(uiStore, (s) => s.setCategoryCollapsed);
	const markCategoryRead = useStore(coreStore, (s) => s.markCategoryRead);

	const isOpen = categoryId ? !collapsedCategories.has(categoryId) : true;
	const setIsOpen = (open: boolean) => {
		if (categoryId) {
			setCategoryCollapsed(categoryId, !open);
		}
	};

	const videoParticipants = useCategoryVideoParticipants(videoChannelIds);

	const handleMarkCategoryRead = () => {
		if (!categoryId) return;
		markCategoryRead(categoryId);
	};

	// Collapsed shows the mentioned channels plus the current one.
	const collapsedChannels = channelsWithMentions.slice();
	if (
		currentChannel &&
		!collapsedChannels.some((c) => c.id === currentChannel.id)
	) {
		collapsedChannels.push(currentChannel);
	}

	const getInitials = (user: (typeof videoParticipants)[0]["user"]) => {
		if (!user) return "?";
		const name = user.profile.displayName;
		if (name) return name.charAt(0).toUpperCase();
		if (user.profile.givenName)
			return user.profile.givenName.charAt(0).toUpperCase();
		return "?";
	};

	const getUserDisplayName = (user: (typeof videoParticipants)[0]["user"]) => {
		if (!user) return "Unknown User";
		return user.profile.displayName || "Unknown User";
	};

	const collapsedChannelIdSet = useMemo(
		() => new Set(collapsedChannels.map((c) => c.id)),
		[collapsedChannels]
	);

	const participantsByChannel = useMemo(() => {
		const grouped = new Map<string, (typeof videoParticipants)[0][]>();
		for (const participant of videoParticipants) {
			if (collapsedChannelIdSet.has(participant.channelId)) continue;
			const existing = grouped.get(participant.channelId) ?? [];
			existing.push(participant);
			grouped.set(participant.channelId, existing);
		}
		return grouped;
	}, [videoParticipants, collapsedChannelIdSet]);

	const filteredParticipantCount = useMemo(() => {
		let count = 0;
		for (const participants of participantsByChannel.values()) {
			count += participants.length;
		}
		return count;
	}, [participantsByChannel]);

	const remainingCount = filteredParticipantCount - MAX_COLLAPSED_AVATARS;

	return (
		<Collapsible
			className="group/collapsible"
			onOpenChange={setIsOpen}
			open={isOpen}
		>
			<SidebarGroup className="relative pt-0 pb-0">
				<ContextMenu>
					<ContextMenuTrigger asChild>
						<SidebarGroupLabel asChild>
							<CollapsibleTrigger asChild>
								<Button
									className={cn(
										"relative w-full justify-start p-2 transition-opacity hover:bg-transparent has-[>svg]:px-2 dark:hover:bg-transparent",
										isCategoryMuted && "opacity-60 hover:opacity-100",
										categoryHasUnread &&
											!isCategoryMuted &&
											"group-data-[state=closed]/collapsible:text-foreground"
									)}
									variant="ghost"
								>
									{categoryName}
									<ChevronDown className="group-data-[state=closed]/collapsible:-rotate-90 size-3.5 transition-transform" />
									{categoryHasUnread && !isCategoryMuted && (
										<div className="-translate-y-1/2 -left-3 invisible absolute top-1/2 size-2 rotate-45 rounded-xs bg-foreground group-data-[state=closed]/collapsible:visible" />
									)}
								</Button>
							</CollapsibleTrigger>
						</SidebarGroupLabel>
					</ContextMenuTrigger>
					<ContextMenuContent>
						<ContextMenuItem
							disabled={!categoryId}
							onClick={handleMarkCategoryRead}
						>
							<CheckCheck className="size-4" />
							Mark Category as Read
						</ContextMenuItem>
						{categoryId &&
							onMute &&
							onUnmute &&
							(isCategoryMuted ? (
								<ContextMenuItem
									onClick={() => onUnmute(categoryId, "category")}
								>
									<Bell className="size-4" />
									Unmute Category
								</ContextMenuItem>
							) : (
								<ContextMenuSub>
									<ContextMenuSubTrigger>
										<BellOff className="size-4" />
										Mute Category
									</ContextMenuSubTrigger>
									<ContextMenuSubContent>
										<ContextMenuItem
											onClick={() => onMute(categoryId, "category", "15m")}
										>
											For 15 Minutes
										</ContextMenuItem>
										<ContextMenuItem
											onClick={() => onMute(categoryId, "category", "1h")}
										>
											For 1 Hour
										</ContextMenuItem>
										<ContextMenuItem
											onClick={() => onMute(categoryId, "category", "8h")}
										>
											For 8 Hours
										</ContextMenuItem>
										<ContextMenuItem
											onClick={() => onMute(categoryId, "category", "24h")}
										>
											For 24 Hours
										</ContextMenuItem>
										<ContextMenuItem
											onClick={() =>
												onMute(categoryId, "category", "indefinite")
											}
										>
											Until I Turn it Back On
										</ContextMenuItem>
									</ContextMenuSubContent>
								</ContextMenuSub>
							))}
					</ContextMenuContent>
				</ContextMenu>

				{!isOpen && filteredParticipantCount > 0 && (
					<div className="flex flex-wrap items-center gap-2 px-2 py-1">
						{(() => {
							let avatarsShown = 0;
							const channelGroups: React.ReactNode[] = [];

							for (const [channelId, participants] of participantsByChannel) {
								const channelName =
									videoChannels.find((c) => c.id === channelId)?.name ??
									"Unknown Channel";
								if (avatarsShown >= MAX_COLLAPSED_AVATARS) break;

								const availableSlots = MAX_COLLAPSED_AVATARS - avatarsShown;
								const visibleInGroup = participants.slice(0, availableSlots);
								const hiddenInGroup =
									participants.length - visibleInGroup.length;
								avatarsShown += visibleInGroup.length;

								channelGroups.push(
									<Tooltip key={channelId}>
										<TooltipTrigger asChild>
											<div className="-space-x-1.5 flex cursor-pointer">
												{visibleInGroup.map((participant) => (
													<Avatar
														className="size-5 border-2 border-background ring-1 ring-border"
														key={participant.firebaseUid}
													>
														{participant.user?.profile.profilePhoto ? (
															<AvatarImage
																alt={getUserDisplayName(participant.user)}
																src={participant.user.profile.profilePhoto}
															/>
														) : null}
														<AvatarFallback className="bg-primary/10 font-medium text-[10px] text-primary">
															{getInitials(participant.user)}
														</AvatarFallback>
													</Avatar>
												))}
												{hiddenInGroup > 0 && (
													<div className="flex size-5 items-center justify-center rounded-full border-2 border-background bg-muted font-medium text-[10px] ring-1 ring-border">
														+{hiddenInGroup}
													</div>
												)}
											</div>
										</TooltipTrigger>
										<TooltipContent
											className="m-2 w-60 overflow-hidden p-0"
											side="bottom"
											sideOffset={4}
										>
											<div className="bg-muted/60 px-3 py-2 dark:bg-muted/40">
												<span className="font-semibold text-foreground text-sm">
													{channelName}
												</span>
											</div>
											<div className="flex flex-col">
												{participants.map((participant) => (
													<div
														className="flex items-center gap-3 bg-popover px-3 py-2"
														key={participant.firebaseUid}
													>
														<Avatar className="size-7 border border-border/50 dark:border-border">
															{participant.user?.profile.profilePhoto ? (
																<AvatarImage
																	alt={getUserDisplayName(participant.user)}
																	src={
																		participant.user.profile.profilePhoto
																	}
																/>
															) : null}
															<AvatarFallback className="bg-muted text-muted-foreground text-xs dark:bg-muted/50">
																{getInitials(participant.user)}
															</AvatarFallback>
														</Avatar>
														<span className="flex-1 truncate text-foreground text-sm">
															{getUserDisplayName(participant.user)}
														</span>
														<div className="flex items-center gap-2">
															{participant.isMicrophoneEnabled ? (
																<Mic className="size-4 text-green-600 dark:text-green-500" />
															) : (
																<MicOff className="size-4 text-muted-foreground/70" />
															)}
															{participant.isCameraEnabled ? (
																<Video className="size-4 text-green-600 dark:text-green-500" />
															) : (
																<VideoOff className="size-4 text-muted-foreground/70" />
															)}
														</div>
													</div>
												))}
											</div>
										</TooltipContent>
									</Tooltip>
								);
							}

							return (
								<>
									{channelGroups}
									{remainingCount > 0 && (
										<div className="flex size-5 items-center justify-center rounded-full border-2 border-background bg-muted font-medium text-[10px] ring-1 ring-border">
											+{remainingCount}
										</div>
									)}
								</>
							);
						})()}
					</div>
				)}

				<CollapsibleContent>{children}</CollapsibleContent>
				{!isOpen && collapsedChannels.length > 0 && (
					<SidebarGroupContent>
						<SidebarMenu>
							{collapsedChannels.map((channel) => (
								<ChannelItem
									channel={channel}
									hasUnread={hasUnread(channel.id)}
									isActive={channel.id === textChannelId}
									isBroadcasting={isBroadcasting}
									isConnectedVoice={channel.id === connectedVoiceChannelId}
									isMuted={isCategoryMuted}
									isTextChatActive={channel.id === textChannelId}
									key={channel.id}
									maxParticipants={maxParticipants}
									mentionCount={channelMentionCounts[channel.id] ?? 0}
									onChannelClick={onChannelClick}
									onChannelKeyDown={onChannelKeyDown}
									onMute={onMute}
									onTextChatClick={onTextChatClick}
									onUnmute={onUnmute}
								/>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				)}
			</SidebarGroup>
		</Collapsible>
	);
}

export function ChannelList() {
	const { state: sidebarState, isMobile, toggleSidebar } = useSidebar();
	const isCollapsed = sidebarState === "collapsed";
	const user = useStore(authStore, (state) => state.user);
	const channelMap = useAccessibleChannels();
	const channels = Array.from(channelMap.values());
	const categoriesMap = useStore(coreStore, (state) => state.categories);
	const categoryMuteStatuses = useStore(
		coreStore,
		(state) => state.categoryMuteStatuses
	);
	const muteTarget = useStore(coreStore, (state) => state.muteTarget);
	const unmuteTarget = useStore(coreStore, (state) => state.unmuteTarget);
	const channelMentionCounts = useMemo(() => {
		const counts: Record<string, number> = {};
		for (const [id, ch] of channelMap) {
			if (ch.mentionCount) counts[id] = ch.mentionCount;
		}
		return counts;
	}, [channelMap]);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	const workspaces = useStore(coreStore, (state) => state.workspaces);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const currentWorkspace = workspaces.get(currentWorkspaceId || "");
	const scheduleSettings =
		(
			currentWorkspace as {
				scheduleSettings?: { scheduleTitle?: string | null };
			}
		)?.scheduleSettings ?? DEFAULT_WORKSPACE_SCHEDULE_SETTINGS;
	const navigate = useStore(uiStore, (state) => state.navigate);
	const handleJoinVoiceChannel = useStore(
		uiStore,
		(state) => state.handleJoinVoiceChannel
	);
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const gridItems = useStore(uiStore, (state) => state.gridItems);

	// The active tab's text channel, which drives the row and voice-chat highlight.
	const textChannelId = (() => {
		const firstItem = gridItems[0];
		if (!firstItem) return null;
		const activeTab = firstItem.tabs[firstItem.activeTabIndex];
		if (
			activeTab?.type === "textChannel" &&
			"channelId" in (activeTab.params ?? {})
		) {
			return (activeTab.params as { channelId: string }).channelId;
		}
		return null;
	})();

	// Drives the green icon, not the row highlight.
	const connectedVoiceChannelId = activeChannel?.id ?? null;

	const isBroadcasting =
		user?.uid === broadcast?.startedBy &&
		broadcast?.status === "live" &&
		!!broadcastRoom;

	const hasSchedule = useHasSchedule();

	const isScheduleActive = (() => {
		const firstItem = gridItems[0];
		if (!firstItem) return false;
		const activeTab = firstItem.tabs[firstItem.activeTabIndex];
		return activeTab?.type === "schedule";
	})();

	const handleScheduleClick = (e: React.MouseEvent) => {
		navigate({ type: "schedule" }, { newTab: e.metaKey || e.ctrlKey });
		if (isMobile) toggleSidebar();
	};

	const hasUnread = (channelId: string) => {
		const channel = channelMap.get(channelId);
		if (!channel?.latestMessageId) return false;

		if (channel.forcedUnreadMessageId) return true;

		const lastSeenMessageId = channel.readStatus;
		if (!lastSeenMessageId) return true;

		return channel.latestMessageId > lastSeenMessageId;
	};

	const isChannelMuted = (channelId: string) => {
		const ch = channelMap.get(channelId);
		if (ch?.muteStatus === undefined) return false;
		if (ch.muteStatus === null) return true;
		return new Date(ch.muteStatus) > new Date();
	};

	const isCategoryMuted = (categoryId: string | null) => {
		if (!categoryId) return false;
		const status = categoryMuteStatuses.get(categoryId);
		if (status === undefined) return false;
		if (status === null) return true;
		return new Date(status) > new Date();
	};

	const isEffectivelyMuted = (channelId: string) => {
		if (isChannelMuted(channelId)) return true;
		const ch = channelMap.get(channelId);
		return isCategoryMuted(ch?.categoryId ?? null);
	};

	const handleMute = (
		targetId: string,
		targetType: "channel" | "category",
		duration: MuteDuration
	) => {
		muteTarget(targetId, targetType, duration);
	};

	const handleUnmute = (
		targetId: string,
		targetType: "channel" | "category"
	) => {
		unmuteTarget(targetId, targetType);
	};

	const handleChannelClick = async (channel: Channel, e: React.MouseEvent) => {
		if (channel.type === "voice_and_text" && !isBroadcasting) {
			await handleJoinVoiceChannel(channel, {
				newTab: e.metaKey || e.ctrlKey,
			});
		} else if (channel.type === "stage" && !isBroadcasting) {
			await handleJoinVoiceChannel(channel, {
				newTab: e.metaKey || e.ctrlKey,
			});
		} else if (channel.type === "text") {
			navigate(
				{
					type: "textChannel",
					params: { channelId: channel.id },
				},
				{ newTab: e.metaKey || e.ctrlKey }
			);
		}
		if (isMobile) {
			toggleSidebar();
		}
	};

	const handleChannelKeyDown = async (
		channel: Channel,
		e: React.KeyboardEvent
	) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			if (channel.type === "voice_and_text" && !isBroadcasting) {
				await handleJoinVoiceChannel(channel, {
					newTab: e.metaKey || e.ctrlKey,
				});
			} else if (channel.type === "stage" && !isBroadcasting) {
				await handleJoinVoiceChannel(channel, {
					newTab: e.metaKey || e.ctrlKey,
				});
			} else if (channel.type === "text") {
				navigate(
					{
						type: "textChannel",
						params: { channelId: channel.id },
					},
					{ newTab: e.metaKey || e.ctrlKey }
				);
			}
		}
	};

	const handleTextChatClick = (channelId: string, e: React.MouseEvent) => {
		navigate(
			{
				type: "textChannel",
				params: { channelId },
			},
			{ newTab: e.metaKey || e.ctrlKey }
		);
		if (isMobile) {
			toggleSidebar();
		}
	};

	const handleCloseThread = (threadId: string) => {
		ws.send(
			JSON.stringify({
				id: ulid(),
				procedure: "thread.close",
				data: { threadId },
			})
		);
	};

	const topLevelChannels = channels.filter((c) => !(c.parentId || c.isThread));

	const getThreadsForChannel = (channelId: string) => {
		const now = new Date();
		const thresholdTime = new Date(
			now.getTime() - THREAD_ACTIVITY_THRESHOLD_HOURS * 60 * 60 * 1000
		);

		return channels.filter((c) => {
			if (!(c.parentId && c.isThread) || c.parentId !== channelId) {
				return false;
			}

			const threadHasUnread = hasUnread(c.id);
			const threadHasMentions = (channelMentionCounts[c.id] ?? 0) > 0;

			if (c.closedAt && !threadHasUnread && !threadHasMentions) {
				return false;
			}

			const lastMessageTime = c.latestMessageAt
				? new Date(c.latestMessageAt)
				: null;
			const isRecentlyActive =
				lastMessageTime !== null && lastMessageTime >= thresholdTime;

			return isRecentlyActive || threadHasUnread || threadHasMentions;
		});
	};

	const channelsByCategoryId = topLevelChannels.reduce<
		Map<string | null, Channel[]>
	>((acc, channel) => {
		const categoryId = channel.categoryId ?? null;
		if (!acc.has(categoryId)) {
			acc.set(categoryId, []);
		}
		acc.get(categoryId)?.push(channel);
		return acc;
	}, new Map());

	// Uncategorized sorts last.
	const sortedCategoryIds = Array.from(channelsByCategoryId.keys())
		.filter((categoryId) => {
			if (categoryId === null) return true; // Always show uncategorized
			const category = categoriesMap.get(categoryId);
			return category?.isVisible ?? true; // Show if isVisible is true or undefined
		})
		.sort((a, b) => {
			if (a === null) return 1;
			if (b === null) return -1;
			const catA = categoriesMap.get(a);
			const catB = categoriesMap.get(b);
			if (!catA) return 1;
			if (!catB) return -1;
			return catA.order - catB.order;
		});

	const Wrapper = isCollapsed ? "div" : Card;

	const isLoading = channels.length === 0 && !!user;

	if (isLoading) {
		return (
			<Wrapper
				className="flex flex-col gap-0 overflow-hidden p-0 shadow-sm"
				data-tour="channels-section"
			>
				<div className="flex flex-col gap-4 p-4">
					<div className="flex flex-col gap-2">
						<Skeleton className="h-4 w-24" />
						<div className="flex flex-col gap-1 pl-2">
							<Skeleton className="h-6 w-full" />
							<Skeleton className="h-6 w-full" />
							<Skeleton className="h-6 w-3/4" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<Skeleton className="h-4 w-32" />
						<div className="flex flex-col gap-1 pl-2">
							<Skeleton className="h-6 w-full" />
							<Skeleton className="h-6 w-5/6" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<Skeleton className="h-4 w-28" />
						<div className="flex flex-col gap-1 pl-2">
							<Skeleton className="h-6 w-full" />
							<Skeleton className="h-6 w-full" />
							<Skeleton className="h-6 w-2/3" />
							<Skeleton className="h-6 w-4/5" />
						</div>
					</div>
				</div>
			</Wrapper>
		);
	}

	return (
		<Wrapper
			className="flex flex-col gap-0 overflow-hidden p-0 shadow-sm"
			data-tour="channels-section"
		>
			<TooltipProvider>
				<ScrollArea className="flex-1 overflow-hidden py-2 pr-2">
					{hasSchedule && (
						<SidebarGroup className="pt-0 pb-0">
							<SidebarGroupContent>
								<SidebarMenu>
									<SidebarMenuItem>
										{/** biome-ignore lint/a11y/useSemanticElements: using div for custom interactive element */}
										<div
											className={cn(
												"group/item flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 hover:bg-accent dark:hover:bg-accent/70",
												isScheduleActive && "bg-accent/75 dark:bg-accent/50"
											)}
											onClick={handleScheduleClick}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleScheduleClick(e as unknown as React.MouseEvent);
												}
											}}
											role="button"
											tabIndex={0}
										>
											<CalendarDays className="mr-1 size-3.5" />
											<span
												className={cn(
													"flex-1 text-muted-foreground text-sm group-hover/item:text-foreground",
													isScheduleActive && "text-foreground"
												)}
											>
												{scheduleSettings.scheduleTitle || "Schedule"}
											</span>
										</div>
									</SidebarMenuItem>
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>
					)}
					{sortedCategoryIds.map((categoryId) => {
						const category = categoryId ? categoriesMap.get(categoryId) : null;
						const categoryChannels = (
							channelsByCategoryId.get(categoryId) ?? []
						).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
						const categoryName = category?.name ?? "Uncategorized";
						const categoryMaxParticipants = category?.maxParticipants ?? null;

						const channelsWithMentions = categoryChannels.filter(
							(c) => channelMentionCounts[c.id] > 0
						);

						const videoChannels = categoryChannels.filter(
							(c) => c.type === "voice_and_text"
						);
						const videoChannelIds = videoChannels.map((c) => c.id);

						const allThreadsInCategory = categoryChannels.flatMap((c) =>
							getThreadsForChannel(c.id)
						);

						const categoryHasUnread =
							categoryChannels.some(
								(c) =>
									hasUnread(c.id) &&
									!channelsWithMentions.some((m) => m.id === c.id)
							) ||
							allThreadsInCategory.some(
								(t) =>
									hasUnread(t.id) && (channelMentionCounts[t.id] ?? 0) === 0
							);

						return (
							<CategoryCollapsible
								categoryHasUnread={categoryHasUnread}
								categoryId={categoryId}
								categoryName={categoryName}
								channelMentionCounts={channelMentionCounts}
								channelsWithMentions={channelsWithMentions}
								connectedVoiceChannelId={connectedVoiceChannelId}
								currentChannel={
									textChannelId
										? (categoryChannels.find((c) => c.id === textChannelId) ??
											null)
										: null
								}
								hasUnread={hasUnread}
								isBroadcasting={isBroadcasting}
								isCategoryMuted={isCategoryMuted(categoryId)}
								key={categoryId ?? "uncategorized"}
								maxParticipants={categoryMaxParticipants}
								onChannelClick={handleChannelClick}
								onChannelKeyDown={handleChannelKeyDown}
								onMute={handleMute}
								onTextChatClick={handleTextChatClick}
								onUnmute={handleUnmute}
								textChannelId={textChannelId}
								videoChannelIds={videoChannelIds}
								videoChannels={videoChannels}
							>
								<SidebarGroupContent>
									<SidebarMenu>
										{categoryChannels.map((channel) => (
											<ChannelItem
												channel={channel}
												channelMentionCounts={channelMentionCounts}
												hasUnread={hasUnread(channel.id)}
												hasUnreadFn={hasUnread}
												isActive={channel.id === textChannelId}
												isBroadcasting={isBroadcasting}
												isConnectedVoice={
													channel.id === connectedVoiceChannelId
												}
												isEffectivelyMuted={isEffectivelyMuted}
												isMuted={isEffectivelyMuted(channel.id)}
												isTextChatActive={channel.id === textChannelId}
												key={channel.id}
												maxParticipants={categoryMaxParticipants}
												mentionCount={channelMentionCounts[channel.id] ?? 0}
												onChannelClick={handleChannelClick}
												onChannelKeyDown={handleChannelKeyDown}
												onCloseThread={handleCloseThread}
												onMute={handleMute}
												onTextChatClick={handleTextChatClick}
												onUnmute={handleUnmute}
												threads={getThreadsForChannel(channel.id)}
											/>
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</CategoryCollapsible>
						);
					})}
				</ScrollArea>
			</TooltipProvider>
		</Wrapper>
	);
}
